import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLoad } from "@/components/erp";
import { Page, Btn, Table, Tr, Td, Field, Loading } from "@/components/kit";

const input = "min-h-[40px] rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-800";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const ROLES = (useLoad("/auth/roles").data || []).filter((r) => r.active).map((r) => [r.key, r.label]);
  const [form, setForm] = useState({ name: "", email: "", role: "" });
  const load = useCallback(async () => setUsers((await api.get("/auth/users")).data), []);
  useEffect(() => { load().catch(() => setUsers([])); }, [load]);
  const run = async (fn, ok) => {
    try {
      const r = await fn();
      toast.success(typeof ok === "function" ? ok(r?.data) : ok);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  if (!users) return <Page title="Users"><Loading /></Page>;
  return (
    <Page title="Users">
      <Table minWidth={760} cols={[{ label: "Name" }, { label: "Email" }, { label: "Role" }, { label: "Status" }, { label: "" }]}>
        {users.map((u) => {
          const mine = u.id === me?.id;
          return (
            <Tr key={u.id} className={u.active === false ? "opacity-60" : ""}>
              <Td>{u.name}{mine && <span className="ml-1 text-xs text-slate-500">(you)</span>}</Td>
              <Td className="text-slate-500">{u.email}</Td>
              <Td>{mine ? u.role_label : (
                <select aria-label="Role" className={input} value={u.role} onChange={(e) => run(() => api.patch(`/auth/users/${u.id}`, { role: e.target.value }), "Role changed")}>
                  {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>)}</Td>
              <Td className={u.active === false ? "text-rose-700" : "text-emerald-700"}>{u.active === false ? "Disabled" : "Active"}</Td>
              <Td>{!mine && (
                <div className="flex flex-wrap justify-end gap-2">
                  <Btn onClick={() => run(() => api.post(`/auth/users/${u.id}/invite`), `Set-password email sent to ${u.email}`)}>Send set-password email</Btn>
                  <Btn onClick={() => run(() => api.patch(`/auth/users/${u.id}`, { active: u.active === false }), u.active === false ? "Enabled" : "Disabled")}>{u.active === false ? "Enable" : "Disable"}</Btn>
                </div>)}</Td>
            </Tr>
          );
        })}
      </Table>
      <section className="mt-6 max-w-3xl rounded-[10px] border border-[#E4E2DE] bg-white p-5">
        <h2 className="text-[15px] font-semibold">Add a user</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_1.4fr_160px]">
          <Field label="Name"><input className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Email"><input type="email" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Role"><select className={input} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}><option value="">— role —</option>{ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
        </div>
        <Btn kind="primary" className="mt-4" disabled={!form.name || !form.email || !form.role}
          onClick={() => run(async () => { const r = await api.post("/auth/users", form); setForm({ name: "", email: "", role: "" }); return r; },
            (d) => (d?.invite_sent ? `${d.email} added - they've been emailed a link to set their password` : `${d?.email} added, but the email didn't send`))}>
          Add user and send invitation
        </Btn>
      </section>
    </Page>
  );
}
